/**
 * Full WhatsApp conversation with one contact (inbound + outbound), loaded
 * lazily when an inbox row is expanded. Backed by nivMessagingAdmin "thread".
 * Error code: CK-10506.
 */
import { useEffect, useRef } from "react";
import { ArrowDown, ArrowUp, Prohibit } from "@phosphor-icons/react";
import ContentLoader from "../../../../components/shared/ContentLoader";
import { useGetNivInboxThreadQuery } from "../../../../store/services/nivApi";
import { formatWhen, OUTCOME_LABELS } from "./messagesShared";

const CONTEXT_LABELS = {
  opt_out_ack: "אישור הסרה",
  lead_welcome: "ברוכים הבאים לליד",
};

const statusLabel = (m) => {
  if (m.direction === "inbound") return null;
  if (m.status === "failed" || m.error) return OUTCOME_LABELS.failed;
  return OUTCOME_LABELS[m.status] || m.status || null;
};

export default function InboxThread({ waId, highlightAt }) {
  const { data, isLoading, isError, error } = useGetNivInboxThreadQuery({ waId }, { skip: !waId });
  const endRef = useRef(null);

  useEffect(() => {
    if (isError) {
      console.error("[CK-10506] Inbox thread load failed:", {
        errorCode: "CK-10506",
        errorMessage: error?.message,
        timestamp: new Date().toISOString(),
      });
    }
  }, [isError, error]);

  // Newest message is the one NiV clicked on — start scrolled to the bottom.
  useEffect(() => {
    if (data?.messages?.length) endRef.current?.scrollIntoView({ block: "nearest" });
  }, [data]);

  if (!waId) return <div className="nmsg-thread-empty">אין מספר וואטסאפ לשורה הזו.</div>;
  if (isLoading) return <ContentLoader size="sm" compact message="טוען שיחה…" />;
  if (isError) return <div className="nmsg-thread-empty">טעינת השיחה נכשלה.</div>;

  const messages = data?.messages || [];
  if (messages.length === 0) return <div className="nmsg-thread-empty">אין הודעות שמורות מול המספר הזה.</div>;

  return (
    <div className="nmsg-thread" dir="rtl" aria-label="שיחת וואטסאפ">
      <div className="nmsg-thread-head">
        <span>
          {messages.length} הודעות{data?.truncated ? " (מוצגות האחרונות)" : ""}
        </span>
        {data?.conversation?.optOut && (
          <span className="nmsg-chip nmsg-chip-bad">
            <Prohibit size={11} weight="fill" /> ביקש/ה הסרה
          </span>
        )}
      </div>
      <ol className="nmsg-thread-list">
        {messages.map((m) => {
          const inbound = m.direction === "inbound";
          const failed = m.status === "failed" || Boolean(m.error);
          const isCurrent = highlightAt && m.at && Math.abs(new Date(m.at) - new Date(highlightAt)) < 2000;
          return (
            <li key={m.id} className={`nmsg-bubble ${inbound ? "is-in" : "is-out"}${failed ? " is-failed" : ""}${isCurrent ? " is-current" : ""}`}>
              <div className="nmsg-bubble-meta">
                {inbound ? <ArrowDown size={11} weight="bold" /> : <ArrowUp size={11} weight="bold" />}
                <span>{inbound ? "הלקוח/ה" : "CliniKit"}</span>
                {!inbound && (m.templateName || m.contextType) && (
                  <span className="nmsg-bubble-tag">{CONTEXT_LABELS[m.contextType] || m.templateName || m.contextType}</span>
                )}
                {m.intent?.intent && <span className="nmsg-bubble-tag">{m.intent.intent}</span>}
                <span className="nmsg-bubble-time">{formatWhen(m.at)}</span>
              </div>
              <p className="nmsg-bubble-text" dir="auto">
                {m.preview || (m.kind === "template" ? "(תבנית ללא תצוגה)" : "—")}
              </p>
              {statusLabel(m) && (
                <span className={`nmsg-bubble-status${failed ? " is-failed" : ""}`} title={m.error || undefined}>
                  {statusLabel(m)}
                  {failed && m.error ? ` · ${m.error}` : ""}
                </span>
              )}
            </li>
          );
        })}
      </ol>
      <div ref={endRef} />
    </div>
  );
}
